// src/components/admin/StatsCards.js
import React from 'react';
import { FileText, CheckCircle2, Clock, TrendingUp, ArrowUpRight } from 'lucide-react';

export default function StatsCards({ stats }) {
  // Calculamos la tasa de conversión una vez para reutilizarla
  const conversionRate = stats.total > 0 ? Math.round((stats.signed / stats.total) * 100) : 0;

  const statItems = [
    { 
      label: 'Total Documentos', 
      sublabel: 'Registrados en el sistema',
      value: stats.total, 
      icon: FileText, 
      theme: 'blue',
      bg: 'bg-blue-50',
      text: 'text-blue-600',
      hoverBorder: 'group-hover:border-blue-200',
      hoverRing: 'group-hover:ring-4 group-hover:ring-blue-500/10'
    },
    { 
      label: 'Firmados', 
      sublabel: 'Procesos completados con éxito',
      value: stats.signed, 
      icon: CheckCircle2, 
      theme: 'emerald',
      bg: 'bg-emerald-50',
      text: 'text-emerald-600',
      hoverBorder: 'group-hover:border-emerald-200',
      hoverRing: 'group-hover:ring-4 group-hover:ring-emerald-500/10'
    },
    { 
      label: 'Pendientes', 
      sublabel: 'Requieren atención o seguimiento',
      value: stats.pending, 
      icon: Clock, 
      theme: 'amber',
      bg: 'bg-amber-50',
      text: 'text-amber-600',
      hoverBorder: 'group-hover:border-amber-200',
      hoverRing: 'group-hover:ring-4 group-hover:ring-amber-500/10'
    },
    { 
      label: 'Tasa de Conversión', 
      sublabel: 'Documentos firmados vs. totales',
      value: `${conversionRate}%`, 
      icon: TrendingUp, 
      theme: 'violet',
      bg: 'bg-violet-50',
      text: 'text-violet-600',
      hoverBorder: 'group-hover:border-violet-200',
      hoverRing: 'group-hover:ring-4 group-hover:ring-violet-500/10',
      isProgress: true,
      progressValue: conversionRate
    }
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
      {statItems.map((stat, idx) => {
        const Icon = stat.icon;
        
        return (
          <div 
            key={idx} 
            className={`
              relative bg-white rounded-2xl border border-slate-200 p-5 lg:p-6 
              transition-all duration-300 ease-out group cursor-default
              hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1
              ${stat.hoverBorder} ${stat.hoverRing}
            `}
          >
            {/* Línea de acento superior sutil al hacer hover */}
            <div className={`absolute top-0 left-6 right-6 h-0.5 bg-${stat.theme}-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

            <div className="flex items-start justify-between mb-4">
              <div className={`w-11 h-11 ${stat.bg} rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:shadow-sm`}>
                <Icon className={`w-5 h-5 ${stat.text}`} />
              </div>
              
              {/* Indicador de tendencia para la tarjeta de conversión */}
              {stat.isProgress && conversionRate > 0 && (
                <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg border border-emerald-100">
                  <ArrowUpRight className="w-3.5 h-3.5" /> 
                  Activo
                </span>
              )}
            </div>

            <div className="space-y-1">
              <p className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight tabular-nums">
                {stat.value}
              </p>
              <p className="text-sm font-semibold text-slate-700">
                {stat.label}
              </p>
              <p className="text-xs text-slate-500 font-medium leading-relaxed">
                {stat.sublabel}
              </p>
            </div>

            {/* Barra de progreso visual solo para la Tasa de Conversión */}
            {stat.isProgress && (
              <div className="mt-5 pt-4 border-t border-slate-100">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Progreso</span>
                  <span className="text-xs font-semibold text-slate-700">{conversionRate}%</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                  <div 
                    className="bg-gradient-to-r from-violet-500 to-violet-400 h-2 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${conversionRate}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}